import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import moment from "moment";
import NumberFormat from "react-number-format";
import { FormGroup, Input, Label } from "reactstrap";
import { CurrencyContext } from "../../context/Context";
import { findAllCurrencies } from "../../helpers/api/currency";
import { fetchOrdinaryExpensesTotal } from "../../helpers/api/finance";
import { fetchAllPages } from "../../helpers/api/pagination";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import {
  convertToUsd,
  formatAmountRounded,
  getCurrencyName,
  getCurrencySymbol,
} from "../../helpers/currencyConversion";
import InputWithLeftSelect from "../common/CurrencyAmountInput";
import WizardError from "../auth/register/WizardError";

const currencySymbol = (currency) => {
  const name = (currency?.name || "").toLowerCase();
  const iso = (currency?.iso || "").toLowerCase();

  if (currency?.symbol) return currency.symbol;
  if (name.includes("boliva") || iso === "ves" || iso === "bs") return "Bs";
  return getCurrencySymbol(currency?.iso);
};

export const useReserveFundAmount = ({ condominiumId, enabled = true }) => {
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const baseIso = (baseCurrency?.iso || "USD").toUpperCase();
  const baseSymbol = baseCurrency?.symbol || "$";
  const today = useMemo(() => moment(), []);

  const [amountMode, setAmountMode] = useState("fixed");
  const [percentage, setPercentage] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedCurrencyId, setSelectedCurrencyId] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [ordinaryExpensesTotal, setOrdinaryExpensesTotal] = useState(0);
  const [pendingConversion, setPendingConversion] = useState(null);
  const sawRateLoadingRef = useRef(false);

  useEffect(() => {
    if (!enabled || !condominiumId) return undefined;
    let isMounted = true;

    fetchOrdinaryExpensesTotal(condominiumId)
      .then((response) => {
        if (isMounted) setOrdinaryExpensesTotal(Number(response?.data?.total ?? 0));
      })
      .catch(() => {
        if (isMounted) setOrdinaryExpensesTotal(0);
      });

    fetchAllPages((page, pageSize) =>
      findAllCurrencies(page, pageSize, { condominium_id: condominiumId })
    )
      .then((response) => {
        if (isMounted) setCurrencies(response?.data || []);
      })
      .catch(() => {
        if (isMounted) setCurrencies([]);
      });

    return () => {
      isMounted = false;
    };
  }, [enabled, condominiumId]);

  const currencyCatalog = useMemo(() => {
    const normalized = currencies.map((item) => ({
      ...item,
      id: String(item.id),
      iso: (item?.iso || "").toUpperCase(),
      isFallbackBase: false,
    }));

    if (!normalized.some((item) => item.iso === baseIso)) {
      normalized.unshift({
        id: baseIso,
        iso: baseIso,
        name: getCurrencyName(baseIso),
        isFallbackBase: true,
      });
    }

    return normalized;
  }, [currencies, baseIso]);

  const baseOption = currencyCatalog.find((item) => item.iso === baseIso);
  const selectedCurrency =
    currencyCatalog.find((item) => item.id === String(selectedCurrencyId)) ||
    baseOption;
  const isBase = selectedCurrency?.iso === baseIso;

  const contextQuoteIso = (curr?.currency?.iso || "").toUpperCase();
  const quoteCurrency =
    currencyCatalog.find((item) => item.iso !== baseIso && item.iso === contextQuoteIso) ||
    currencyCatalog.find((item) => item.iso !== baseIso);

  const rateCurrencyFor = (currency) =>
    amountMode === "fixed" && currency && currency.iso !== baseIso
      ? currency
      : quoteCurrency;
  const rateCurrency = rateCurrencyFor(selectedCurrency);
  const rateCurrencyIsBase = !rateCurrency || rateCurrency.iso === baseIso;

  const { rateResolution, isRateLoading, appliedExchangeRate } =
    useExchangeRateForDate({
      currencyId: enabled && rateCurrency ? rateCurrency.id : null,
      isUsdCurrency: rateCurrencyIsBase,
      date: today,
      activeExchangeRate: Number(curr?.value ?? 0),
      condominiumId,
    });

  const computedFromPercentage =
    (Number(percentage || 0) / 100) * ordinaryExpensesTotal;

  const baseAmount =
    amountMode === "percentage"
      ? Number(computedFromPercentage.toFixed(2))
      : convertToUsd(amount, isBase, appliedExchangeRate);

  const effectiveDateKey =
    rateResolution?.usedDateKey || rateResolution?.selectedDateKey;
  const rateDateLabel = (effectiveDateKey ? moment(effectiveDateKey) : moment()).format(
    "DD/MM/YYYY"
  );

  const changeCurrency = (currencyId) => {
    const target =
      currencyCatalog.find((item) => item.id === String(currencyId)) || baseOption;
    const currentBase = Number(baseAmount);

    sawRateLoadingRef.current = false;
    setPendingConversion(
      baseAmount !== "" && currentBase > 0
        ? {
            baseAmount: currentBase,
            waitForRate: rateCurrencyFor(target)?.id !== rateCurrency?.id,
          }
        : null
    );
    setSelectedCurrencyId(currencyId);
    setAmount("");
  };

  useEffect(() => {
    if (!pendingConversion) return;

    if (isBase) {
      setAmount(Number(pendingConversion.baseAmount.toFixed(2)));
      setPendingConversion(null);
      return;
    }

    if (isRateLoading) {
      sawRateLoadingRef.current = true;
      return;
    }

    if (pendingConversion.waitForRate && !sawRateLoadingRef.current) return;

    if (Number(appliedExchangeRate) > 0) {
      setAmount(
        Number((pendingConversion.baseAmount * Number(appliedExchangeRate)).toFixed(2))
      );
    }
    setPendingConversion(null);
  }, [pendingConversion, isBase, isRateLoading, appliedExchangeRate]);

  const reset = useCallback((initialAmount = "") => {
    setAmountMode("fixed");
    setPercentage("");
    setAmount(initialAmount);
    setSelectedCurrencyId("");
    setPendingConversion(null);
  }, []);

  return {
    amountMode,
    setAmountMode,
    percentage,
    setPercentage,
    amount,
    setAmount,
    changeCurrency,
    currencyCatalog,
    selectedCurrency,
    isBase,
    quoteCurrency,
    ordinaryExpensesTotal,
    computedFromPercentage,
    baseAmount,
    baseIso,
    baseSymbol,
    isRateLoading,
    appliedExchangeRate,
    rateDateLabel,
    reset,
  };
};

const ReserveFundAmountFields = ({ state, label = "Monto", error = undefined }) => {
  const {
    amountMode,
    setAmountMode,
    percentage,
    setPercentage,
    amount,
    setAmount,
    changeCurrency,
    currencyCatalog,
    selectedCurrency,
    isBase,
    quoteCurrency,
    ordinaryExpensesTotal,
    computedFromPercentage,
    baseAmount,
    baseIso,
    baseSymbol,
    isRateLoading,
    appliedExchangeRate,
    rateDateLabel,
  } = state;

  const hasQuoteRate = Boolean(quoteCurrency) && Number(appliedExchangeRate) > 0;

  const fixedHelpText = (() => {
    if (!Number(amount)) return "";
    if (isBase) {
      if (!hasQuoteRate || isRateLoading) return "";
      return `Tasa ${rateDateLabel}: ${formatAmountRounded(
        appliedExchangeRate,
        2
      )} | Equivalente aproximado: ${currencySymbol(quoteCurrency)} ${formatAmountRounded(
        Number(amount) * Number(appliedExchangeRate),
        2
      )} ${quoteCurrency.iso}`;
    }
    if (isRateLoading) return "Consultando tasa...";
    if (!appliedExchangeRate) return "";

    return `Tasa ${rateDateLabel}: ${formatAmountRounded(
      appliedExchangeRate,
      2
    )} | Equivalente aproximado: ${baseSymbol}${formatAmountRounded(
      baseAmount,
      2
    )} ${baseIso}`;
  })();

  return (
    <>
      <FormGroup>
        <Label for="reserve-fund-amount-mode">
          ¿Cómo quieres ingresar el monto?
        </Label>
        <Input
          id="reserve-fund-amount-mode"
          type="select"
          value={amountMode}
          onChange={(event) => setAmountMode(event.target.value)}
        >
          <option value="fixed">Monto fijo</option>
          <option value="percentage">Porcentaje de gastos ordinarios</option>
        </Input>
      </FormGroup>
      {amountMode === "percentage" ? (
        <>
          <FormGroup>
            <Label for="reserve-fund-percentage">Porcentaje</Label>
            <NumberFormat
              id="reserve-fund-percentage"
              suffix=" %"
              decimalScale={2}
              allowNegative={false}
              value={percentage}
              className="form-control"
              placeholder="Porcentaje"
              onValueChange={({ value }) => setPercentage(value)}
            />
            <p className="fs--1 text-600 mt-1 mb-0">
              Total de gastos ordinarios activos del condominio:{" "}
              <NumberFormat
                displayType="text"
                prefix={`${baseSymbol} `}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                value={ordinaryExpensesTotal}
              />
            </p>
          </FormGroup>
          <FormGroup>
            <Label>{`${label} (calculado)`}</Label>
            <NumberFormat
              disabled
              prefix={`${baseSymbol} `}
              thousandSeparator
              decimalScale={2}
              fixedDecimalScale
              value={computedFromPercentage}
              className={`${error ? "border-danger " : ""}form-control`}
            />
            {quoteCurrency && (
              <p className="fs--1 text-600 mt-1 mb-0">
                {isRateLoading ? (
                  "Consultando tasa..."
                ) : hasQuoteRate ? (
                  <>
                    Equivalente en {quoteCurrency.iso}:{" "}
                    <NumberFormat
                      displayType="text"
                      prefix={`${currencySymbol(quoteCurrency)} `}
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      value={computedFromPercentage * Number(appliedExchangeRate)}
                    />{" "}
                    (tasa del {rateDateLabel}:{" "}
                    {formatAmountRounded(appliedExchangeRate, 2)})
                  </>
                ) : (
                  `No hay una tasa de ${quoteCurrency.iso} para hoy.`
                )}
              </p>
            )}
            <WizardError error={error} className="fs--1 font-weight-normal d-block" />
          </FormGroup>
        </>
      ) : (
        <FormGroup>
          <Label>{label}</Label>
          <InputWithLeftSelect
            selectValue={selectedCurrency?.id || ""}
            onSelectChange={(event) => changeCurrency(event.target.value)}
            selectOptions={currencyCatalog.map((item) => ({
              value: String(item.id),
              label: `${item.iso} (${item.name})`,
            }))}
            selectAriaLabel="Moneda del monto"
            selectMinWidth="120px"
            helpText={fixedHelpText}
          >
            <NumberFormat
              key={selectedCurrency?.id}
              value={amount}
              thousandSeparator
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              className={`${error ? "border-danger " : ""}form-control border-left-0`}
              placeholder="0.00"
              prefix={`${currencySymbol(selectedCurrency)} `}
              onValueChange={({ value }) => setAmount(value)}
            />
          </InputWithLeftSelect>
          {!isBase && (
            <p className="fs--1 text-600 mt-1 mb-0">
              Se guarda el equivalente en {baseIso} a la tasa de hoy.
            </p>
          )}
          <WizardError error={error} className="fs--1 font-weight-normal d-block" />
        </FormGroup>
      )}
    </>
  );
};

ReserveFundAmountFields.propTypes = {
  state: PropTypes.object.isRequired,
  label: PropTypes.string,
  error: PropTypes.string,
};


export default ReserveFundAmountFields;
